import { CKButton, CKInputChips, CKLayoutGrid } from 'clearkit';
import { useEffect, useState } from 'react';

const validateReferrer = (referrer: string) => {
  if (referrer === '') return;

  const referrerUrl = referrer.includes('//') ? referrer : `http://${referrer}`;

  try {
    const validReferrer = new URL(referrerUrl).hostname.replace('%2A.', '');

    if (!validReferrer || !validReferrer.includes('.')) {
      return;
    }

    return validReferrer;
  } catch (error) {
    return;
  }
};

const validateReferrers = (referrers: string[]) => {
  return referrers.filter((referrer) => validateReferrer(referrer));
};

type JavaScriptReferrersProps = {
  className?: string;
  onSave: (referrers: string[]) => void;
  loading?: boolean;
  referrers: string[];
  showHeading?: boolean;
};

const JavaScriptReferrers = ({
  className,
  loading = false,
  referrers: referrersProp,
  onSave: onSaveProp,
  showHeading = true,
}: JavaScriptReferrersProps) => {
  const [formDirty, setFormDirty] = useState(false);
  const [referrers, setReferrers] = useState<string[]>([]);

  useEffect(() => {
    if (referrersProp && referrersProp.length) {
      setReferrers(referrersProp);
    }
  }, [referrersProp]);

  const handleChange = (referrers: string[]) => {
    const validReferrers = validateReferrers(referrers);

    if (!validReferrers) return;

    setFormDirty(true);

    setReferrers(validReferrers);
  };

  const onSave = async () => {
    await onSaveProp(referrers);

    setFormDirty(false);
  };

  return (
    <div className={className}>
      {showHeading && (
        <>
          <h2 className="text-base font-medium text-gray-600">
            Allowed Referrers
          </h2>
          <p className="mt-2 mb-4 text-sm text-gray-600">
            Add the domains you want to allow the snippet to load on below. The{' '}
            <a
              className="ck-link"
              href="https://help.clearbit.com/hc/en-us/articles/360010019734-Getting-started-with-clearbit-js"
              rel="noopener noreferrer"
              target="_blank"
            >
              installation guide
            </a>{' '}
            has complete instructions.
          </p>
        </>
      )}
      <CKLayoutGrid gridTemplateColumns="1fr auto">
        <CKInputChips
          maxHeight="14rem"
          onChange={handleChange}
          placeholder="Enter domains for referrers"
          shouldOverflow={false}
          splitTokens={[',', ' ']}
          value={referrers}
        />
        <CKButton
          isDisabled={!formDirty || loading}
          onClick={onSave}
          variant="bold"
          variantColor="blue"
        >
          {loading ? 'Saving...' : 'Save'}
        </CKButton>
      </CKLayoutGrid>
    </div>
  );
};

export default JavaScriptReferrers;
